import { useState } from 'react';
import { useCart } from '@/hooks/useCart';
import { useQuoteModal } from '@/hooks/useQuoteModal';

const camposPetroleros = [
  { nombre: 'Ayacucho', descripcion: 'Faja Petrolífera del Orinoco' },
  { nombre: 'Carabobo', descripcion: 'Faja Petrolífera del Orinoco' },
  { nombre: 'Junín', descripcion: 'Faja Petrolífera del Orinoco' },
  { nombre: 'Boyacá', descripcion: 'Faja Petrolífera del Orinoco' },
];

const beneficios = [
  {
    icono: 'ri-flashlight-line',
    titulo: 'Alta Eficiencia de Separación',
    descripcion: 'Rompe emulsiones agua-crudo con rapidez excepcional, incluso en crudos pesados y extrapesados de la Faja.',
  },
  {
    icono: 'ri-drop-line',
    titulo: 'Baja Dosificación',
    descripcion: 'Formulación concentrada que reduce el volumen requerido por barril, optimizando costos operativos.',
  },
  {
    icono: 'ri-temp-hot-line',
    titulo: 'Estabilidad Térmica',
    descripcion: 'Mantiene su efectividad en amplios rangos de temperatura, desde superficie hasta condiciones de fondo de pozo.',
  },
  {
    icono: 'ri-shield-check-line',
    titulo: 'Compatibilidad Total',
    descripcion: 'Formulado para ser compatible con sistemas de producción existentes sin interferir con otros químicos del proceso.',
  },
];

const caracteristicasTecnicas = [
  'Fabricado por Rixin Corp, China',
  'Aprobado en laboratorios de PDVSA',
  'Validado en pruebas de botella en cada campo',
  'Producto ya formulado, listo para usar',
  'Resultados consistentes lote tras lote',
  'Soporte técnico en sitio por Poly Alphas',
];

interface HomeProductCardProps {
  id: string;
  name: string;
  volume: string;
  price: number;
  image: string;
  imageAlt: string;
  features: string[];
  iconClass: string;
  iconBgClass: string;
  checkColor: string;
}

function HomeProductCard({
  id,
  name,
  volume,
  price,
  image,
  imageAlt,
  features,
  iconClass,
  iconBgClass,
  checkColor,
}: HomeProductCardProps) {
  const { addItem, setIsOpen } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    addItem({
      id,
      name,
      price,
      quantity,
      unit: 'unidad',
      image,
      volume,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="bg-background-50 rounded-lg border border-background-200/60 overflow-hidden group hover:border-primary-300/60 transition-all duration-300">
      <div className="aspect-[4/3] overflow-hidden bg-background-200/40">
        <img
          src={image}
          alt={imageAlt}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${iconBgClass}`}>
            <i className={`${iconClass} text-lg`} />
          </div>
          <div className="min-w-0">
            <h4 className="font-heading text-lg font-bold text-foreground-950 truncate">{name}</h4>
            <p className="text-foreground-500 text-xs">{volume}</p>
            <p className="text-primary-600 font-heading text-base font-bold mt-1">
              ${price.toLocaleString()} <span className="text-foreground-400 text-xs font-normal">USD</span>
            </p>
          </div>
        </div>
        <div className="space-y-2 mb-5">
          {features.map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm text-foreground-600">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`ri-check-line ${checkColor} text-xs`} />
              </div>
              <span className="leading-relaxed">{f}</span>
            </div>
          ))}
        </div>

        {/* Quantity + Add to cart */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex items-center gap-1 bg-background-100 rounded-md overflow-hidden border border-background-200/60 self-start sm:self-auto">
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="Disminuir cantidad"
            >
              <i className="ri-subtract-line text-xs" />
            </button>
            <span className="w-10 text-center text-sm font-semibold text-foreground-800">{quantity}</span>
            <button
              onClick={() => setQuantity((q) => q + 1)}
              className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="Aumentar cantidad"
            >
              <i className="ri-add-line text-xs" />
            </button>
          </div>
          <button
            onClick={handleAdd}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              added
                ? 'bg-accent-500 text-background-50'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
          >
            <i className={`${added ? 'ri-check-line' : 'ri-shopping-cart-2-line'} text-sm`} />
            {added ? 'Agregado' : 'Añadir al carrito'}
          </button>
        </div>

        {/* High demand badge */}
        <div className="mt-3 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-semibold">
            <i className="ri-fire-line text-[10px]" />
            Producto de alta demanda
          </span>
          <button
            onClick={() => setIsOpen(true)}
            className="text-primary-600 text-xs font-medium hover:underline cursor-pointer"
          >
            Ver carrito
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProductsSection() {
  const { open } = useQuoteModal();
  return (
    <section id="products" className="bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="text-center mb-14 md:mb-18">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-6">
              <i className="ri-stack-line text-sm w-4 h-4 flex items-center justify-center"></i>
              Producto Importado
            </div>
            <p className="text-foreground-500 text-xs md:text-sm font-medium tracking-wide uppercase mb-3">
              RIXIN CORP CHINA → RESINVENCA RESINAS DE VENEZUELA C.A. VENEZUELA
            </p>
            <h2 className="font-heading text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-3 md:mb-4">
              Demulsificante{' '}
              <span className="text-primary-500">Importado y Listo para Usar</span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              Demulsificante ya formulado por Rixin Corp, importado directamente desde China y validado 
              en los laboratorios de PDVSA. Eficacia comprobada en los campos más exigentes de Venezuela.
            </p>
          </div>

          {/* Main product card - full width */}
          <div className="bg-background-50 rounded-lg border border-background-200/60 p-6 md:p-10 mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
              {/* Left - Product info */}
              <div className="lg:col-span-3">
                <div className="flex items-start gap-4 mb-6 min-w-0">
                  <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 flex-shrink-0">
                    <i className="ri-oil-line text-2xl"></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                      Demulsificante para Crudo
                    </h3>
                    <p className="text-foreground-500 text-sm">
                      Formulación de alto rendimiento fabricada por Rixin Corp, China, para la separación agua-crudo en sistemas de producción petrolera
                    </p>
                  </div>
                </div>

                <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-6 text-left sm:text-justify">
                  Nuestro demulsificante es importado directamente desde las instalaciones de{' '}
                  <strong className="text-foreground-900">Rixin Corp en China</strong>, donde es formulado 
                  y producido bajo estrictos estándares internacionales. El producto llega a Venezuela 
                  ya terminado, listo para usar, diseñado específicamente para las características de los 
                  crudos venezolanos, especialmente los crudos pesados y extrapesados de la Faja Petrolífera del Orinoco.
                </p>

                {/* PDVSA Badge */}
                <div className="inline-flex items-center gap-3 bg-primary-50 border border-primary-200/50 rounded-lg px-5 py-3 mb-8 max-w-full">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0">
                    <i className="ri-government-line text-lg"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-primary-700 font-semibold text-sm">Aprobado por Laboratorios PDVSA</p>
                    <p className="text-primary-500 text-xs">Certificado en pruebas de botella y análisis fisicoquímico en Venezuela</p>
                  </div>
                </div>

                {/* Technical features */}
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-2">
                  {caracteristicasTecnicas.map((caract) => (
                    <div key={caract} className="flex items-center gap-2 min-w-0">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-double-line text-primary-500 text-sm"></i>
                      </div>
                      <span className="text-foreground-700 text-xs truncate">{caract}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - Campos Petroleros */}
              <div className="lg:col-span-2">
                <div className="bg-background-100 rounded-lg p-6 md:p-8 h-full">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-2-line text-primary-600 text-sm"></i>
                    </div>
                    <h4 className="text-foreground-950 font-heading text-base font-semibold">
                      Campos Petroleros Aprobados
                    </h4>
                  </div>

                  <p className="text-foreground-500 text-xs mb-6">
                    Faja Petrolífera del Orinoco
                  </p>

                  <div className="space-y-3">
                    {camposPetroleros.map((campo) => (
                      <div
                        key={campo.nombre}
                        className="flex items-center gap-4 p-3.5 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-300/60 transition-all duration-300 min-w-0"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className="ri-oil-line text-base"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-foreground-950 font-semibold text-sm truncate">{campo.nombre}</p>
                          <p className="text-foreground-500 text-xs truncate">{campo.descripcion}</p>
                        </div>
                        <div className="ml-auto w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                          <i className="ri-check-line text-xs"></i>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-4 border-t border-background-200/60 text-center">
                    <p className="text-foreground-500 text-xs">
                      <span className="text-primary-600 font-bold text-base">4</span> campos activos
                    </p>
                    <p className="text-foreground-400 text-xs mt-0.5">con aprobación vigente</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Video destacado — Resultado en campo */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                <i className="ri-price-tag-3-line text-sm w-4 h-4 flex items-center justify-center"></i>
                El Mejor Precio Del Mercado Venezolano
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                Presentaciones <span className="text-primary-500">Disponibles</span>
              </h3>
              <p className="text-foreground-500 text-sm max-w-xl mx-auto">
                Producto importado desde China, empacado y listo para entrega en Venezuela. 
                Consulte por precios especiales para compras recurrentes.
              </p>
            </div>

            {/* Packaging cards */}
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-6 md:gap-8">
              <HomeProductCard
                id="tambor-demulsificante"
                name="Tambor Demulsificante"
                volume="55 Galones / 208 Litros"
                price={950}
                image="https://readdy.ai/api/search-image?query=Industrial%2055%20gallon%20blue%20steel%20chemical%20drum%20with%20metal%20lid%20and%20rings%2C%20professional%20product%20photography%20on%20clean%20white%20studio%20background%2C%20soft%20diffused%20lighting%2C%20minimal%20composition%2C%20commercial%20industrial%20grade%20packaging%2C%20high%20detail%20texture%20on%20metal%20surface%2C%20slight%20shadow%20on%20ground&width=800&height=600&seq=tambor-industrial-01&orientation=landscape"
                imageAlt="Tambor metálico industrial de 55 galones para demulsificante RESINVENCA importado de China"
                features={[
                  'Acero industrial con recubrimiento interior anticorrosivo',
                  'Sellado hermético para transporte seguro desde China',
                  'Ideal para pedidos pequeños y prueba en campo',
                ]}
                iconClass="ri-archive-line"
                iconBgClass="bg-accent-100 text-accent-600"
                checkColor="text-accent-500"
              />

              <HomeProductCard
                id="totem-demulsificante"
                name="Totem Demulsificante"
                volume="1.000 Litros"
                price={4500}
                image="https://readdy.ai/api/search-image?query=White%201000%20liter%20IBC%20intermediate%20bulk%20container%20tote%20with%20galvanized%20metal%20cage%20and%20plastic%20pallet%20base%2C%20industrial%20chemical%20storage%20tank%2C%20professional%20product%20photography%20on%20clean%20white%20studio%20background%2C%20soft%20diffused%20lighting%2C%20minimal%20composition%2C%20high%20detail%20showing%20valve%20and%20cage%20structure%2C%20commercial%20grade&width=800&height=600&seq=ibc-container-01&orientation=landscape"
                imageAlt="IBC contenedor industrial de 1000 litros para demulsificante RESINVENCA importado de China"
                features={[
                  'Tanque plástico de alta resistencia con jaula metálica',
                  'Válvula de descarga inferior para fácil dosificación',
                  'Mayor volumen, menor costo por litro importado',
                ]}
                iconClass="ri-bring-to-front"
                iconBgClass="bg-primary-100 text-primary-600"
                checkColor="text-primary-500"
              />
            </div>
          </div>

          {/* Revendedores Section */}
          <div className="mb-16 bg-background-50 -mx-4 md:-mx-8 lg:-mx-16 px-4 md:px-8 lg:px-16 py-14 md:py-20 overflow-hidden">
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-5">
                  <i className="ri-hand-coin-line text-sm w-4 h-4 flex items-center justify-center"></i>
                  Oportunidad de Negocio
                </div>
                <h3 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                  Programa de{' '}
                  <span className="text-accent-500">Revendedores</span>
                </h3>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                  Producto terminado importado desde China, puesto en Venezuela. Usted compra, revende y gana. 
                  Sin complicaciones logísticas, sin inversión en formulación. Nosotros importamos, usted factura.
                </p>
              </div>

              {/* Price Highlight */}
              <div className="text-center mb-12">
                <div className="inline-block bg-accent-100/50 border border-accent-200/60 rounded-lg px-6 md:px-8 py-5 max-w-full">
                  <p className="text-foreground-500 text-xs md:text-sm mb-1">Precio de Revendedor</p>
                  <div className="font-heading text-3xl md:text-5xl font-bold text-foreground-950">
                    $950<span className="text-accent-500 text-base md:text-xl font-normal ml-1">/ Tambor</span>
                  </div>
                  <p className="text-foreground-500 text-xs mt-2">Importación directa desde Rixin Corp, China a Venezuela • Listo para revender</p>
                </div>
              </div>

              {/* Profit Cards Grid */}
              <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-5 md:gap-6 mb-12">
                {/* Card 1 - Compra */}
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-shopping-cart-2-line text-lg"></i>
                  </div>
                  <p className="text-foreground-500 text-xs font-medium mb-1">Usted Compra</p>
                  <p className="text-foreground-950 font-heading text-xl font-bold mb-2">Desde $950</p>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    Precio fijo por tambor de 55 galones. Sin costos ocultos. Producto importado desde China por RESINVENCA RESINAS DE VENEZUELA C.A., terminado y certificado, listo para entregar a su cliente final.
                  </p>
                </div>

                {/* Card 2 - Revende */}
                <div className="bg-background-100 rounded-lg p-6 border border-accent-200/60 relative mt-3 md:mt-0">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-500 text-background-50 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    Mayor Rentabilidad
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4 mt-1">
                    <i className="ri-money-dollar-circle-line text-lg"></i>
                  </div>
                  <p className="text-foreground-500 text-xs font-medium mb-1">Usted Revende</p>
                  <p className="text-foreground-950 font-heading text-xl font-bold mb-2">$1,100 – $1,350</p>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    Precio de mercado comprobado para demulsificantes de esta calidad. Margen estimado de ganancia por tambor en el mercado venezolano actual.
                  </p>
                </div>

                {/* Card 3 - Gana */}
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-line-chart-line text-lg"></i>
                  </div>
                  <p className="text-foreground-500 text-xs font-medium mb-1">Usted Gana</p>
                  <p className="text-foreground-950 font-heading text-xl font-bold mb-2">$150 – $400</p>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    Por cada tambor. Con 20 tambores al mes, sus ganancias mensuales pueden superar los <strong className="text-primary-600">$8,000 USD</strong> líquidos.
                  </p>
                </div>
              </div>

              {/* PDVSA Contract Highlight */}
              <div className="bg-primary-100/50 border border-primary-200/60 rounded-lg p-5 md:p-7 mb-12">
                <div className="flex flex-col md:flex-row items-center gap-5 text-center md:text-left">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-government-line text-2xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-primary-700 text-xs font-medium uppercase tracking-wide mb-1">Máxima Rentabilidad</p>
                    <h4 className="text-foreground-950 font-heading text-lg md:text-xl font-bold mb-2">
                      Para Empresas con Contrato en <span className="text-primary-600">PDVSA</span>
                    </h4>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      El tambor de demulsificante importado y aprobado se vende a un precio de mercado de{' '}
                      <strong className="text-primary-600 text-base">$2,500 USD</strong>. Con un costo de importación de solo $950, 
                      su ganancia neta por tambor asciende a{' '}
                      <strong className="text-primary-600 text-base">$1,550 USD</strong>. 
                      La aprobación en laboratorios PDVSA es su llave de entrada al cliente mejor pagador de Venezuela.
                    </p>
                  </div>
                  <div className="bg-primary-100/70 rounded-lg px-6 py-4 text-center flex-shrink-0">
                    <p className="text-foreground-500 text-xs mb-1">Ganancia por Tambor</p>
                    <p className="text-primary-600 font-heading text-2xl md:text-3xl font-bold">$1,550</p>
                    <p className="text-foreground-500 text-xs mt-1">USD</p>
                  </div>
                </div>
              </div>

              {/* Profit projection table */}
              <div className="bg-foreground-950 rounded-lg p-4 md:p-8 border border-foreground-800/60 mb-12 overflow-x-auto">
                <h4 className="text-background-50 font-heading text-base font-semibold mb-5 text-center">Proyección de Ganancias Mensuales</h4>
                <table className="w-full text-left min-w-[540px]">
                  <thead>
                    <tr className="border-b border-foreground-800/60">
                      <th className="text-foreground-400 text-xs font-medium py-3 pr-4">Tambores / Mes</th>
                      <th className="text-foreground-400 text-xs font-medium py-3 pr-4">Inversión</th>
                      <th className="text-foreground-400 text-xs font-medium py-3 pr-4">Venta Estimada</th>
                      <th className="text-foreground-400 text-xs font-medium py-3">Ganancia Neta</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-foreground-800/30">
                      <td className="text-background-50 text-sm font-semibold py-3 pr-4">5</td>
                      <td className="text-foreground-400 text-sm py-3 pr-4">$4,750</td>
                      <td className="text-foreground-400 text-sm py-3 pr-4">$5,500 – $6,750</td>
                      <td className="text-accent-400 text-sm font-bold py-3">$750 – $2,000</td>
                    </tr>
                    <tr className="border-b border-foreground-800/30">
                      <td className="text-background-50 text-sm font-semibold py-3 pr-4">10</td>
                      <td className="text-foreground-400 text-sm py-3 pr-4">$9,500</td>
                      <td className="text-foreground-400 text-sm py-3 pr-4">$11,000 – $13,500</td>
                      <td className="text-accent-400 text-sm font-bold py-3">$1,500 – $4,000</td>
                    </tr>
                    <tr className="border-b border-foreground-800/30">
                      <td className="text-background-50 text-sm font-semibold py-3 pr-4">20</td>
                      <td className="text-foreground-400 text-sm py-3 pr-4">$19,000</td>
                      <td className="text-foreground-400 text-sm py-3 pr-4">$22,000 – $27,000</td>
                      <td className="text-accent-400 text-sm font-bold py-3">$3,000 – $8,000</td>
                    </tr>
                    <tr>
                      <td className="text-background-50 text-sm font-semibold py-3 pr-4">50</td>
                      <td className="text-foreground-400 text-sm py-3 pr-4">$47,500</td>
                      <td className="text-foreground-400 text-sm py-3 pr-4">$55,000 – $67,500</td>
                      <td className="text-accent-400 text-sm font-bold py-3">$7,500 – $20,000</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-2 xs:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-10">
                <div className="bg-background-100 rounded-lg p-5 border border-background-200/60 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                    <i className="ri-flask-line text-xl"></i>
                  </div>
                  <h5 className="text-foreground-950 font-heading text-sm font-semibold mb-1">Asesores Químicos</h5>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    Ingenieros especializados de Poly Alphas acompañan cada venta con soporte técnico en sitio para sus clientes.
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg p-5 border border-background-200/60 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                    <i className="ri-team-line text-xl"></i>
                  </div>
                  <h5 className="text-foreground-950 font-heading text-sm font-semibold mb-1">Fabricante Rixin Corp</h5>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    Producto formulado en China por Rixin Corp, con resultados comprobados y estándares internacionales.
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg p-5 border border-background-200/60 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-3">
                    <i className="ri-medal-line text-xl"></i>
                  </div>
                  <h5 className="text-foreground-950 font-heading text-sm font-semibold mb-1">Mejores Resultados</h5>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    El demulsificante con la mayor eficiencia de separación del mercado venezolano. Sus clientes lo notan desde el primer lote.
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg p-5 border border-background-200/60 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-700 mx-auto mb-3">
                    <i className="ri-truck-line text-xl"></i>
                  </div>
                  <h5 className="text-foreground-950 font-heading text-sm font-semibold mb-1">Logística Resuelta</h5>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    Producto puesto en Venezuela. RESINVENCA se encarga de toda la importación desde China. Usted solo recibe, almacena y entrega.
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={open}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Quiero Ser Revendedor
                  <i className="ri-arrow-right-line"></i>
                </button>
                <p className="text-foreground-500 text-xs mt-4">
                  Escríbanos ahora y reciba asesoría personalizada para iniciar su negocio
                </p>
              </div>
            </div>
          </div>

          {/* Benefits grid */}
          <div className="mb-12">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              Beneficios{' '}
              <span className="text-primary-500">Clave</span>
            </h3>
            <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {beneficios.map((beneficio) => (
                <div
                  key={beneficio.titulo}
                  className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/60 hover:border-primary-200/50 transition-all duration-300"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-4">
                    <i className={`${beneficio.icono} text-lg`}></i>
                  </div>
                  <h4 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                    {beneficio.titulo}
                  </h4>
                  <p className="text-foreground-600 text-xs md:text-sm leading-relaxed text-left sm:text-justify">
                    {beneficio.descripcion}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              type="button"
              onClick={open}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
            >
              Solicitar Cotización del Demulsificante
              <i className="ri-arrow-right-line"></i>
            </button>
            <p className="text-foreground-400 text-xs mt-4">
              Producto importado desde Rixin Corp, China. Consulte por disponibilidad y precios.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 mt-3">
              <a href="tel:+584129080594" className="inline-flex items-center gap-1.5 text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-line text-sm"></i>
                +58 412 908 0594
              </a>
              <a href="tel:+584121019460" className="inline-flex items-center gap-1.5 text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-line text-sm"></i>
                +58 412 101 9460
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}